/**
 * Shared loading components for consistent UI.
 * Canonical location: blocks/loading. Re-exported from ui/loading for backward compatibility.
 */

import React from 'react';
import { cn } from '@/lib/utils';

const SpinnerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('animate-spin', className)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="status"
    aria-label="Loading"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export interface InlineSpinnerProps {
  className?: string;
  size?: 'sm' | 'md';
}

export const InlineSpinner: React.FC<InlineSpinnerProps> = ({ className, size = 'sm' }) => (
  <span className={cn('inline-flex shrink-0', className)} role="status" aria-label="Loading">
    <SpinnerIcon
      className={cn(
        'text-primary',
        size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'
      )}
    />
  </span>
);

export interface PanelLoadingProps {
  message?: string;
  className?: string;
}

export const PanelLoading: React.FC<PanelLoadingProps> = ({
  message = 'Loading...',
  className,
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center min-h-[200px] py-12 px-4 text-muted-foreground text-sm',
      className
    )}
    role="status"
    aria-label="Loading"
  >
    <SpinnerIcon className="h-8 w-8 text-primary mb-3" />
    {message && <p>{message}</p>}
  </div>
);

export interface PageLoadingProps {
  message?: string;
  className?: string;
}

export const PageLoading: React.FC<PageLoadingProps> = ({
  message = 'Loading...',
  className,
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center py-16 px-4 text-muted-foreground text-sm',
      className
    )}
    role="status"
    aria-label="Loading"
  >
    <SpinnerIcon className="h-10 w-10 text-primary mb-4" />
    {message && <p>{message}</p>}
  </div>
);

export interface SkeletonRowProps {
  className?: string;
  cells?: number;
}

export const SkeletonRow: React.FC<SkeletonRowProps> = ({ className, cells = 5 }) => (
  <tr className={cn('animate-pulse', className)}>
    {Array.from({ length: cells }).map((_, i) => (
      <td key={i} className="px-3 py-2">
        <div className="h-4 bg-muted rounded w-full max-w-[120px]" />
      </td>
    ))}
  </tr>
);

export interface SkeletonTableProps {
  className?: string;
  rows?: number;
  cols?: number;
  headers?: string[];
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  className,
  rows = 5,
  cols = 5,
  headers,
}) => (
  <div className={cn('border border-border rounded-lg overflow-hidden', className)}>
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b border-border bg-muted/50">
          {Array.from({ length: cols }).map((_, i) => (
            <th key={i} className="px-3 py-2 text-left text-sm font-medium text-muted-foreground">
              {headers?.[i] ?? ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex} className="border-b border-border animate-pulse">
            {Array.from({ length: cols }).map((_, colIndex) => (
              <td key={colIndex} className="px-3 py-2">
                <div className="h-4 bg-muted rounded w-full max-w-[120px]" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
